import React, { useEffect, useState } from 'react';
import {
  Typography, Button, Box, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, CircularProgress, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TableSortLabel, Stack, Checkbox
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import { getCoachSkiFleet, addSkiToFleet, updateSkiInFleet, deleteSkiFromFleet } from '../api';
import { useAuth } from '../AuthContext';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

const BRANDS = [
  'Fischer',
  'Salomon',
  'Atomic',
  'Rossignol',
  'Madshus',
];

function SkiFleetForm({ open, onClose, onSave, initial, defaultTechnique }) {
  const [form, setForm] = useState(initial || {
    ski_name: '', serial_number: '', brand: '', fa: '', hr: '', mf: '', h4: '', grind: '', mold: '', base: '', ski_length_cm:'', year_made: '', type: defaultTechnique || 'classic', rossi_flex: '', rossi_height: '', rossi_stiffness: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    setForm(initial || {
      ski_name: '', serial_number: '', brand: '', fa: '', hr: '', mf: '', h4: '', grind: '', mold: '', base: '', ski_length_cm:'', year_made: '', type: defaultTechnique || 'classic', rossi_flex: '', rossi_height: '', rossi_stiffness: ''
    });
    setError('');
    setLoading(false);
  }, [open, initial, defaultTechnique]);
  
  const handleChange = e => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };
  
  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Determine which flex and camber fields to show based on brand
  const showFischerFields = form.brand === 'Fischer';
  const showSalomonAtomicFields = ['Salomon', 'Atomic'].includes(form.brand);
  const showRossignolFields = form.brand === 'Rossignol';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initial ? 'Edit Ski' : 'Add Ski to Fleet'}</DialogTitle>
      <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
        <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField label="Ski Number" name="ski_name" type="text" value={form.ski_name} onChange={handleChange} required fullWidth />
          <TextField label="Serial Number" name="serial_number" type="text" value={form.serial_number} onChange={handleChange} fullWidth />
          <TextField select label="Brand" name="brand" value={form.brand} onChange={handleChange} required fullWidth>
            {BRANDS.map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
          </TextField>
          
          {/* Fischer-specific fields */}
          {showFischerFields && (
            <>
              <TextField label="FA (Fischer Flex)" name="fa" type="number" value={form.fa} onChange={handleChange} fullWidth />
              <TextField label="HR (Fischer Camber Height)" name="hr" type="number" value={form.hr} onChange={handleChange} fullWidth />
            </>
          )}
          
          {/* Salomon/Atomic-specific fields */}
          {showSalomonAtomicFields && (
            <>
              <TextField label="MF (Salomon/Atomic Flex)" name="mf" type="number" value={form.mf} onChange={handleChange} fullWidth />
              <TextField label="H4 (Salomon/Atomic Camber Height)" name="h4" type="number" value={form.h4} onChange={handleChange} fullWidth />
            </>
          )}

          {/* Rossignol-specific fields */}
          {showRossignolFields && (
            <>
              <TextField label="Flex (Rossignol Flex)" name="rossi_flex" type="number" value={form.rossi_flex} onChange={handleChange} fullWidth />
              <TextField label="Height (Rossignol Camber Height)" name="rossi_height" type="number" value={form.rossi_height} onChange={handleChange} fullWidth />
              <TextField select label="Mold (Rossignol Name)" name="mold" value={form.mold} onChange={handleChange} fullWidth>
                <MenuItem value="S1">S1</MenuItem>
                <MenuItem value="S2">S2</MenuItem>
                <MenuItem value="S3">S3</MenuItem>
                <MenuItem value="SX">SX</MenuItem>
                <MenuItem value="C1">C1</MenuItem>
                <MenuItem value="C2">C2</MenuItem>
                <MenuItem value="C3">C3</MenuItem>
                <MenuItem value="Zero">Zero</MenuItem>
                <MenuItem value="skin">Skin</MenuItem>
                <MenuItem value="Double Pole">Double Pole</MenuItem>
              </TextField>
              <TextField select label="Stiffness" name="rossi_stiffness" value={form.rossi_stiffness} onChange={handleChange} fullWidth>
                <MenuItem value="Soft">Soft</MenuItem>
                <MenuItem value="Medium">Medium</MenuItem>
                <MenuItem value="Stiff">Stiff</MenuItem>
              </TextField>
            </>
          )}
          
          <TextField label="Grind" name="grind" value={form.grind} onChange={handleChange} fullWidth />
          {!showRossignolFields && <TextField label="Mold" name="mold" value={form.mold} onChange={handleChange} fullWidth />}
          <TextField label="Base" name="base" value={form.base} onChange={handleChange} fullWidth />
          <TextField label="Length (cm)" name="ski_length_cm" type="text" value={form.ski_length_cm} onChange={handleChange} fullWidth />
          <TextField label="Year Made" name="year_made" type="number" value={form.year_made} onChange={handleChange} fullWidth />
          <TextField select label="Type" name="type" value={form.type} onChange={handleChange} required fullWidth>
            <MenuItem value="classic">Classic</MenuItem>
            <MenuItem value="skate">Skate</MenuItem>
          </TextField>
          {error && <Typography color="error">{error}</Typography>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function SkisTable({ skis, onEdit, onDelete, onDetail, selectedIds, onToggleSelect, showCheckBox }) {
  const [deleteDialog, setDeleteDialog] = useState({ open: false, ski: null });
  const [showSwipeHint, setShowSwipeHint] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Separate sorting state for each brand
  const [sortStates, setSortStates] = useState({});

  const handleSort = (brand, property) => {
    const currentState = sortStates[brand] || { orderBy: 'ski_name', order: 'asc' };
    const isAsc = currentState.orderBy === property && currentState.order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    
    setSortStates(prev => ({
      ...prev,
      [brand]: { orderBy: property, order: newOrder }
    }));
  };

  const handleDeleteClick = (ski) => setDeleteDialog({ open: true, ski });
  const handleDeleteConfirm = () => {
    onDelete(deleteDialog.ski.id);
    setDeleteDialog({ open: false, ski: null });
  };
  const handleDeleteCancel = () => setDeleteDialog({ open: false, ski: null });

  // Hide swipe hint after first scroll/tap
  const handleTableScroll = () => setShowSwipeHint(false);

  // Group skis by brand and sort brands by number of skis (most to least)
  const groupSkisByBrand = (skis) => {
    const groups = {};
    skis.forEach(ski => {
      if (!groups[ski.brand]) {
        groups[ski.brand] = [];
      }
      groups[ski.brand].push(ski);
    });
    
    // Sort brands by number of skis (most to least)
    return Object.entries(groups).sort(([, a], [, b]) => b.length - a.length);
  };

  const renderBrandTable = (brandSkis, brand) => {
    const isFischer = brand === 'Fischer';
    const isSalomonAtomic = ['Salomon', 'Atomic'].includes(brand);
    const isRossignol = brand === 'Rossignol';
    
    // Separate skis by type
    const skateSkis = brandSkis.filter(ski => ski.type === 'skate');
    const classicSkis = brandSkis.filter(ski => ski.type === 'classic');
    
    const renderTypeTable = (skis, type) => {
      // Get sorting state for this brand
      const brandSortState = sortStates[brand] || { orderBy: 'ski_name', order: 'asc' };
      
      // Sort skis within this type table
      const sortedSkis = [...skis].sort((a, b) => {
        if (brandSortState.orderBy === 'fa' || brandSortState.orderBy === 'hr' || brandSortState.orderBy === 'mf' || brandSortState.orderBy === 'h4') {
          const aVal = parseFloat(a[brandSortState.orderBy]) || 0;
          const bVal = parseFloat(b[brandSortState.orderBy]) || 0;
          return brandSortState.order === 'asc' ? aVal - bVal : bVal - aVal;
        }
        // Default: ski_name - handle as string for natural sorting
        const aNum = parseInt(a.ski_name) || 0;
        const bNum = parseInt(b.ski_name) || 0;
        return brandSortState.order === 'asc' ? aNum - bNum : bNum - aNum;
      });
      
      if (sortedSkis.length === 0) return null;
      
      return (
        <Box key={type} sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, color: 'text.secondary', fontWeight: 600 }}>
            {type === 'skate' ? 'Skate' : 'Classic'} ({sortedSkis.length})
          </Typography>
          <TableContainer
          component={Paper}
          sx={{
            borderRadius: 2,
            boxShadow: 2,
            width: '100%',
            minWidth: 320,
            overflowX: 'auto',
            position: 'relative',
          }}
          onScroll={isMobile ? handleTableScroll : undefined}
        >
          <Table sx={{ width: isMobile ? 760 : '100%' }} size={isMobile ? 'small' : 'medium'}>
            <TableHead>
              <TableRow sx={{ background: '#f5f5f5' }}>
                {showCheckBox && <TableCell padding="checkbox"></TableCell>}
                <TableCell sx={isMobile ? { px: 1, py: 0.5, fontSize: 13 } : {}}>Ski #</TableCell>
                {isFischer && (
                  <>
                    <TableCell sx={isMobile ? { px: 1, py: 0.5, fontSize: 13 } : {}} sortDirection={brandSortState.orderBy === 'fa' ? brandSortState.order : false}>
                      <TableSortLabel
                        active={brandSortState.orderBy === 'fa'}
                        direction={brandSortState.orderBy === 'fa' ? brandSortState.order : 'asc'}
                        onClick={() => handleSort(brand, 'fa')}
                      >
                        FA
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={isMobile ? { px: 1, py: 0.5, fontSize: 13 } : {}} sortDirection={brandSortState.orderBy === 'hr' ? brandSortState.order : false}>
                      <TableSortLabel
                        active={brandSortState.orderBy === 'hr'}
                        direction={brandSortState.orderBy === 'hr' ? brandSortState.order : 'asc'}
                        onClick={() => handleSort(brand, 'hr')}
                      >
                        HR
                      </TableSortLabel>
                    </TableCell>
                  </>
                )}
                {isSalomonAtomic && (
                  <>
                    <TableCell sx={isMobile ? { px: 1, py: 0.5, fontSize: 13 } : {}} sortDirection={brandSortState.orderBy === 'mf' ? brandSortState.order : false}>
                      <TableSortLabel
                        active={brandSortState.orderBy === 'mf'}
                        direction={brandSortState.orderBy === 'mf' ? brandSortState.order : 'asc'}
                        onClick={() => handleSort(brand, 'mf')}
                      >
                        MF
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={isMobile ? { px: 1, py: 0.5, fontSize: 13 } : {}} sortDirection={brandSortState.orderBy === 'h4' ? brandSortState.order : false}>
                      <TableSortLabel
                        active={brandSortState.orderBy === 'h4'}
                        direction={brandSortState.orderBy === 'h4' ? brandSortState.order : 'asc'}
                        onClick={() => handleSort(brand, 'h4')}
                      >
                        H4
                      </TableSortLabel>
                    </TableCell>
                  </>
                )}
                {isRossignol && (
                  <>
                    <TableCell sx={isMobile ? { px: 1, py: 0.5, fontSize: 13 } : {}} sortDirection={brandSortState.orderBy === 'rossi_flex' ? brandSortState.order : false}>
                      <TableSortLabel
                        active={brandSortState.orderBy === 'rossi_flex'}
                        direction={brandSortState.orderBy === 'rossi_flex' ? brandSortState.order : 'asc'}
                        onClick={() => handleSort(brand, 'rossi_flex')}
                      >
                        Flex
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={isMobile ? { px: 1, py: 0.5, fontSize: 13 } : {}} sortDirection={brandSortState.orderBy === 'rossi_height' ? brandSortState.order : false}>
                      <TableSortLabel
                        active={brandSortState.orderBy === 'rossi_height'}
                        direction={brandSortState.orderBy === 'rossi_height' ? brandSortState.order : 'asc'}
                        onClick={() => handleSort(brand, 'rossi_height')}
                      >
                        Height
                      </TableSortLabel>
                    </TableCell>
                  </>
                )}
                <TableCell sx={isMobile ? { px: 1, py: 0.5, fontSize: 13 } : {}}>Grind</TableCell>
                <TableCell sx={isMobile ? { px: 1, py: 0.5, fontSize: 13 } : {}}>Mold</TableCell>
                <TableCell sx={isMobile ? { px: 1, py: 0.5, fontSize: 13 } : {}}>Base</TableCell>
                <TableCell sx={isMobile ? { px: 1, py: 0.5, fontSize: 13 } : {}}>Year</TableCell>
                <TableCell align="center" sx={isMobile ? { px: 1, py: 0.5, fontSize: 13 } : {}}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedSkis.map((ski) => (
                <TableRow key={ski.id} hover>
                {showCheckBox && 
                  <TableCell padding="checkbox">
                    <Checkbox color="primary" checked={!!selectedIds?.has(ski.id)} onChange={() => onToggleSelect?.(ski.id)} />
                  </TableCell>
                }
                  <TableCell sx={isMobile ? { px: 1, py: 0.5, fontSize: 13, maxWidth: 60, overflow: 'hidden', textOverflow: 'ellipsis' } : {}}>{ski.ski_name}</TableCell>
                  {isFischer && (
                    <>
                      <TableCell sx={isMobile ? { px: 1, py: 0.5, fontSize: 13, maxWidth: 50, overflow: 'hidden', textOverflow: 'ellipsis' } : {}}>{ski.fa}</TableCell>
                      <TableCell sx={isMobile ? { px: 1, py: 0.5, fontSize: 13, maxWidth: 50, overflow: 'hidden', textOverflow: 'ellipsis' } : {}}>{ski.hr}</TableCell>
                    </>
                  )}
                  {isSalomonAtomic && (
                    <>
                      <TableCell sx={isMobile ? { px: 1, py: 0.5, fontSize: 13, maxWidth: 50, overflow: 'hidden', textOverflow: 'ellipsis' } : {}}>{ski.mf}</TableCell>
                      <TableCell sx={isMobile ? { px: 1, py: 0.5, fontSize: 13, maxWidth: 50, overflow: 'hidden', textOverflow: 'ellipsis' } : {}}>{ski.h4}</TableCell>
                    </>
                  )}
                  {isRossignol && (
                    <>
                      <TableCell sx={isMobile ? { px: 1, py: 0.5, fontSize: 13, maxWidth: 50, overflow: 'hidden', textOverflow: 'ellipsis' } : {}}>{ski.rossi_flex}</TableCell>
                      <TableCell sx={isMobile ? { px: 1, py: 0.5, fontSize: 13, maxWidth: 50, overflow: 'hidden', textOverflow: 'ellipsis' } : {}}>{ski.rossi_height}</TableCell>
                    </>
                  )}
                  <TableCell sx={isMobile ? { px: 1, py: 0.5, fontSize: 13, maxWidth: 60, overflow: 'hidden', textOverflow: 'ellipsis' } : {}}>{ski.grind}</TableCell>
                  <TableCell sx={isMobile ? { px: 1, py: 0.5, fontSize: 13, maxWidth: 60, overflow: 'hidden', textOverflow: 'ellipsis' } : {}}>{ski.mold}</TableCell>
                  <TableCell sx={isMobile ? { px: 1, py: 0.5, fontSize: 13, maxWidth: 60, overflow: 'hidden', textOverflow: 'ellipsis' } : {}}>{ski.base}</TableCell>
                  <TableCell sx={isMobile ? { px: 1, py: 0.5, fontSize: 13, maxWidth: 60, overflow: 'hidden', textOverflow: 'ellipsis' } : {}}>{ski.year_made}</TableCell>
                  <TableCell align="center" sx={isMobile ? { px: 1, py: 0.5, fontSize: 13 } : {}}>
                    <Stack direction="row" spacing={isMobile ? 1.5 : 1} justifyContent="center">
                      <IconButton size={isMobile ? 'medium' : 'small'} onClick={() => onDetail(ski)}><InfoIcon fontSize={isMobile ? 'medium' : 'small'} /></IconButton>
                      <IconButton size={isMobile ? 'medium' : 'small'} onClick={() => onEdit(ski)}><EditIcon fontSize={isMobile ? 'medium' : 'small'} /></IconButton>
                      <IconButton size={isMobile ? 'medium' : 'small'} color="error" onClick={() => handleDeleteClick(ski)}><DeleteIcon fontSize={isMobile ? 'medium' : 'small'} /></IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
    };
    
    // Return the brand section with separate skate and classic tables
    return (
      <Box key={brand} sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
          {brand} Skis ({brandSkis.length})
        </Typography>
        {renderTypeTable(skateSkis, 'skate')}
        {renderTypeTable(classicSkis, 'classic')}
      </Box>
    );
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {isMobile && showSwipeHint && (
        <Box sx={{
          position: 'absolute',
          top: 8,
          left: 0,
          right: 0,
          zIndex: 2,
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}>
          <Box sx={{
            bgcolor: 'rgba(255,255,255,0.9)',
            px: 2, py: 0.5, borderRadius: 2, boxShadow: 1, fontSize: 13,
            color: 'text.secondary',
          }}>
            Swipe to scroll table &rarr;
          </Box>
        </Box>
      )}
      
      {groupSkisByBrand(skis).map(([brand, brandSkis]) => 
        renderBrandTable(brandSkis, brand)
      )}
      
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete ski {deleteDialog.ski?.ski_name}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default function CoachSkiFleetSection() {
  const [fleet, setFleet] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, ski: null });
  const { user } = useAuth();

  useEffect(() => {
    fetchFleet();
  }, []);

  const fetchFleet = async () => {
    setLoading(true);
    try {
      const data = await getCoachSkiFleet();
      setFleet(data);
    } catch (err) {
      console.error('Error fetching ski fleet:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => setModal({ open: true, ski: null });
  const handleEdit = ski => setModal({ open: true, ski });
  const handleDelete = async (id) => {
    try {
      await deleteSkiFromFleet(id);
      fetchFleet();
    } catch (err) {
      alert('Failed to delete ski: ' + err.message);
    }
  };

  const handleDetail = (ski) => {
    // For now, just open edit dialog
    setModal({ open: true, ski });
  };

  const handleSave = async (form) => {
    try {
      if (modal.ski) {
        await updateSkiInFleet(modal.ski.id, form);
      } else {
        await addSkiToFleet(form);
      }
      setModal({ open: false, ski: null });
      fetchFleet();
    } catch (err) {
      throw err;
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Ski Fleet</Typography>
        <Button variant="contained" onClick={handleAdd}>Add Ski to Fleet</Button>
      </Box>
      
      {loading ? (
        <CircularProgress />
      ) : fleet.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No skis in your fleet yet
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Add skis to your test fleet to use them when creating wax tests.
          </Typography>
          <Button variant="contained" onClick={handleAdd}>
            Add Your First Ski
          </Button>
        </Paper>
      ) : (
        <SkisTable 
          skis={fleet} 
          onEdit={handleEdit} 
          onDelete={handleDelete}
          onDetail={handleDetail}
          showCheckBox={false}
        />
      )}
      
      <SkiFleetForm 
        open={modal.open} 
        onClose={() => setModal({ open: false, ski: null })} 
        onSave={handleSave} 
        initial={modal.ski}
        defaultTechnique={user?.default_technique}
      />
    </Box>
  );
}

